import * as React from "react";
import { cn } from "../lib/utils.js";
import { slaState, msUntilDue, formatMss } from "../lib/sla.js";
import type { SlaState } from "../lib/sla.js";

/**
 * Shared 1Hz ticker (UIUX §3.2 / §5.4). An inbox page renders dozens of
 * live timers (SLA countdowns, waiting/overdue chips); one `setInterval`
 * per timer burns the browser — `WaitingTimer`'s own comment calls this
 * out. `TickerProvider` runs a single interval and fans the current
 * epoch-ms out through context, so N timers cost N cheap re-renders per
 * second instead of N intervals.
 */
const TickerContext = React.createContext<number | null>(null);

export function TickerProvider({ children }: { children: React.ReactNode }) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => {
      // Background tabs don't need a live repaint; the next visible
      // tick catches up (Date.now() is re-read, not accumulated).
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return <TickerContext.Provider value={now}>{children}</TickerContext.Provider>;
}

/**
 * Current time in epoch-ms, updated at 1Hz. Uses the shared
 * `TickerProvider` interval when one is mounted; falls back to a local
 * interval so standalone renders (tests, storybook-style usage) still tick.
 */
export function useNowTick(): number {
  const shared = React.useContext(TickerContext);
  const [localNow, setLocalNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (shared !== null) return;
    const id = setInterval(() => setLocalNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [shared]);
  return shared ?? localNow;
}

export interface SlaTimerProps {
  dueAt: string | null | undefined;
  /** Override "now" for testing; defaults to the shared ticker. */
  now?: Date;
  className?: string;
}

const stateStyles: Record<SlaState, string> = {
  fresh: "bg-[hsl(var(--triage-ai-bg))] text-[hsl(var(--triage-ai))]",
  // Darkened amber fg for WCAG AA ≥4.5:1 on the warm background (§8.2).
  warn: "bg-[hsl(var(--triage-warm-bg))] text-[hsl(var(--triage-warm-fg))]",
  breach: "bg-[hsl(var(--triage-hot-bg))] text-[hsl(var(--triage-hot))]",
};

/**
 * SlaTimer — a chip showing time remaining / overdue for a lead SLA.
 * - null/undefined dueAt → renders nothing (no SLA set).
 * - Ticks every second via the shared TickerProvider (one interval per
 *   page, not one per row).
 * WHY: This is the "answer now" countdown — the color change from green→amber→red
 * is the primary urgency signal; getting the threshold wrong makes a breached
 * lead look fine.
 */
export function SlaTimer({ dueAt, now: nowProp, className }: SlaTimerProps) {
  const tickMs = useNowTick();

  if (!dueAt) return null;

  const now = nowProp ?? new Date(tickMs);
  const state = slaState(dueAt, now);
  const ms = msUntilDue(dueAt, now);

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums leading-none",
        stateStyles[state],
        className,
      )}
    >
      ⚡ {formatMss(ms)}
    </span>
  );
}
